import { useCallback } from 'react';
import type { TFunction } from 'i18next';
import type { UseMutationResult } from '@tanstack/react-query';
import type {
  ReservationBoard,
  ReservationBooking,
} from '../services/reservation-system/reservation-board.service';
import {
  RS_MAX_RESOURCE_COUNT,
  suggestNextRsResourceName,
} from '../utils/reservationBoardCalendar.util';

/**
 * 所有者向け: 項目の追加・改名・削除（確認ダイアログ含む）。
 */
export function useRsBoardResourceAdmin({
  board,
  bookings,
  editResourceIndex,
  editResourceName,
  setEditResourceIndex,
  setEditResourceName,
  updateResourceMutation,
  flashError,
  t,
}: {
  board: ReservationBoard | undefined;
  bookings: ReservationBooking[];
  editResourceIndex: number | null;
  editResourceName: string;
  setEditResourceIndex: (value: number | null) => void;
  setEditResourceName: (value: string) => void;
  updateResourceMutation: UseMutationResult<ReservationBoard, unknown, string[], unknown>;
  flashError: (text: string) => void;
  t: TFunction;
}) {
  const openResourceEdit = useCallback(
    (index: number, name: string) => {
      setEditResourceIndex(index);
      setEditResourceName(name);
    },
    [setEditResourceIndex, setEditResourceName]
  );

  const submitResourceEdit = useCallback(() => {
    if (!board || editResourceIndex === null) return;
    const trimmed = editResourceName.trim();
    if (!trimmed) {
      flashError(
        t('reservation.board.resourceNameRequired', { defaultValue: '項目名を入力してください。' })
      );
      return;
    }
    if (board.resourceNames.some((name, i) => i !== editResourceIndex && name === trimmed)) {
      flashError(
        t('reservation.board.resourceNameDuplicate', { defaultValue: '同じ項目名が既にあります。' })
      );
      return;
    }
    updateResourceMutation.mutate(
      board.resourceNames.map((name, i) => (i === editResourceIndex ? trimmed : name))
    );
  }, [
    board,
    editResourceIndex,
    editResourceName,
    flashError,
    t,
    updateResourceMutation,
  ]);

  const addResource = useCallback(() => {
    if (!board || board.resourceNames.length >= RS_MAX_RESOURCE_COUNT) {
      flashError(
        t('reservation.board.resourceMaxReached', {
          defaultValue: `項目は最大${RS_MAX_RESOURCE_COUNT}件までです。`,
          count: RS_MAX_RESOURCE_COUNT,
        })
      );
      return;
    }
    const formatSuggestedResourceName = (n: number) =>
      t('reservation.board.suggestedResourceName', { n, defaultValue: `項目${n}` });
    updateResourceMutation.mutate([
      ...board.resourceNames,
      suggestNextRsResourceName(board.resourceNames, formatSuggestedResourceName),
    ]);
  }, [board, flashError, t, updateResourceMutation]);

  const removeResource = useCallback(
    (index: number) => {
      if (!board || board.resourceNames.length <= 1) {
        flashError(
          t('reservation.board.resourceMinRequired', { defaultValue: '項目は1件以上必要です。' })
        );
        return;
      }
      const name = board.resourceNames[index];
      const bookingCount = bookings.filter((booking) => booking.resourceName === name).length;
      if (
        bookingCount > 0 &&
        !window.confirm(
          t('reservation.board.confirmRemoveResourceWithBookings', {
            defaultValue:
              '「{{name}}」に予約が{{count}}件あります。項目を削除すると予約も削除されます。よろしいですか？',
            name,
            count: bookingCount,
          })
        )
      ) {
        return;
      }
      if (
        bookingCount === 0 &&
        !window.confirm(
          t('reservation.board.confirmRemoveResource', {
            defaultValue: '「{{name}}」を削除します。よろしいですか？',
            name,
          })
        )
      ) {
        return;
      }
      updateResourceMutation.mutate(board.resourceNames.filter((_, i) => i !== index));
    },
    [board, bookings, flashError, t, updateResourceMutation]
  );

  return {
    openResourceEdit,
    submitResourceEdit,
    addResource,
    removeResource,
  };
}
